import React, { useCallback, useContext, useState } from 'react';
import { LoadingButton } from '@mui/lab';
import {
  Button, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle,
  TextField,
} from '@mui/material';
import PropTypes from 'prop-types';

import useToast from '../../../../hooks/useToast';
import PodContext from '../../../../../context/PodContext';
import texts from './texts.json';

const CreateCoachPodModal = ({
  open, setOpen, loading, handlePodCreate,
}) => {
  const [podName, setPodName] = useState('');
  const { pods } = useContext(PodContext);
  const { showToast } = useToast();

  const handleClose = useCallback(() => {
    setOpen(false);
    setPodName('');
  }, [setOpen]);

  const createPod = useCallback(async () => {
    if (!podName.trim()) {
      return;
    }

    const podNameExists = pods.some((pod) => pod.data.name.toLowerCase() === podName.toLowerCase());

    if (podNameExists) {
      showToast(texts.podNameAlreadyExistsWarning, { error: true });
      return;
    }

    await handlePodCreate(podName);
    setPodName('');
    setOpen(false);
  }, [podName, handlePodCreate, setOpen, showToast, pods]);

  const handlePodNaming = (e) => {
    const { value } = e.target;
    const firstCharValid = value.length === 0 || /^[a-zA-Z]/.test(value);
    const entireStringValid = /^[a-zA-Z]([a-zA-Z0-9-]|\s(?!\s))*[a-zA-Z0-9-]?$/.test(value) || value === '';

    if (firstCharValid && entireStringValid) {
      setPodName(value);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
    >
      <DialogTitle id="create-pod">
        {texts.createPodTitle}
      </DialogTitle>
      <DialogContent>
        <DialogContentText>
          {texts.createPodDescription}
        </DialogContentText>
        <TextField
          autoFocus
          required
          margin="dense"
          id="name"
          label="Pod Name"
          fullWidth
          variant="standard"
          onChange={handlePodNaming}
          value={podName}
          helperText={texts.createPodHelpText}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose}>{texts.cancel}</Button>
        <LoadingButton
          loading={loading}
          disabled={loading}
          variant="contained"
          onClick={createPod}
          autoFocus
        >
          {texts.create}
        </LoadingButton>
      </DialogActions>
    </Dialog>
  );
};

CreateCoachPodModal.propTypes = {
  open: PropTypes.bool.isRequired,
  loading: PropTypes.bool.isRequired,
  setOpen: PropTypes.func.isRequired,
  handlePodCreate: PropTypes.func.isRequired,
};

export default CreateCoachPodModal;
